import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import type { SpassuCodeApi } from '../../api';
import type { Activity } from '../../api/types';
import {
  formatUserEntityRef,
  getCatalogEntityDisplayName,
} from '../../utils/format';

function useProjectLabels(api: Pick<SpassuCodeApi, 'listProjects'>) {
  const [labels, setLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    api
      .listProjects()
      .then(projects => {
        if (!cancelled)
          setLabels(Object.fromEntries(projects.map(p => [p.id, p.name])));
      })
      .catch(() => {
        /* Keep fallback labels when project lookup fails. */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);
  return useCallback((id: string) => labels[id] ?? id, [labels]);
}

function catalogLabels(refs: string[], items: (Entity | undefined)[]) {
  const labels: Record<string, string> = {};
  refs.forEach((ref, index) => {
    const label = getCatalogEntityDisplayName(items[index]);
    if (label) labels[ref] = label;
  });
  return labels;
}

function useCreatorLabels(refs: string[]) {
  const catalogApi = useApi(catalogApiRef);
  const [labels, setLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    if (refs.length === 0) return undefined;
    let cancelled = false;
    catalogApi
      .getEntitiesByRefs({
        entityRefs: refs,
        fields: ['metadata.name', 'metadata.title', 'spec.profile.displayName'],
      })
      .then(response => {
        if (cancelled) return;
        const next = catalogLabels(refs, response.items);
        if (Object.keys(next).length > 0)
          setLabels(prev => ({ ...prev, ...next }));
      })
      .catch(() => {
        /* Keep fallback labels when catalog lookup fails. */
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, refs]);
  return useCallback(
    (id: string) => labels[id] ?? formatUserEntityRef(id),
    [labels],
  );
}

export function usePortfolioLabels(
  api: Pick<SpassuCodeApi, 'listProjects'>,
  activities: Activity[],
  creators: string[],
) {
  const refs = useMemo(
    () =>
      Array.from(
        new Set(
          [...activities.map(activity => activity.userId), ...creators].filter(
            id => Boolean(id) && id !== 'anonymous',
          ),
        ),
      ),
    [activities, creators],
  );
  const getProjectLabel = useProjectLabels(api);
  const getUserLabel = useCreatorLabels(refs);
  return { getProjectLabel, getUserLabel };
}
export type PortfolioLabels = ReturnType<typeof usePortfolioLabels>;
